import { useMemo, useRef, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import Button from "@/components/base/Button";
import { supabase } from "@/lib/supabase";
import { useVehiculos } from "@/hooks/usePublicContent";
import { tipoCargaOptions, tipoServicioOptions, unidadTipoOptions } from "@/lib/solicitudOptions";
import type { SolicitudInput } from "@/types";

type Status = "idle" | "loading" | "success" | "error";

interface FieldErrors {
  [key: string]: string;
}

export default function SolicitudForm() {
  const fleet = useVehiculos().filter((vehicle) => vehicle.activo);
  const [searchParams] = useSearchParams();
  const unidadParam = searchParams.get("unidad") ?? "";
  const servicioParam = searchParams.get("servicio") ?? "";

  const defaultUnidadId = fleet.some((vehicle) => vehicle.id === unidadParam) ? unidadParam : "";
  const defaultTipoServicio = tipoServicioOptions.includes(servicioParam) ? servicioParam : "";

  const [tipoServicio, setTipoServicio] = useState(defaultTipoServicio);
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");
  const [errors, setErrors] = useState<FieldErrors>({});
  const [numero, setNumero] = useState("");
  const [asesoria, setAsesoria] = useState(false);

  const formRef = useRef<HTMLFormElement>(null);
  const resultRef = useRef<HTMLDivElement>(null);

  const mostrarPersonas = useMemo(
    () => tipoServicio === "Transporte de personas" || tipoServicio === "Evento",
    [tipoServicio],
  );

  const resetFormulario = () => {
    formRef.current?.reset();
    setTipoServicio(defaultTipoServicio);
    setAsesoria(false);
    setErrors({});
    setFormError("");
    setNumero("");
    setStatus("idle");
  };

  const baseField =
    "w-full rounded-md border bg-background-50 px-3.5 py-2.5 text-sm text-foreground-800 placeholder:text-foreground-400 transition-colors focus:outline-none focus:ring-2";

  const fieldClass = (name: string) =>
    `${baseField} ${
      errors[name]
        ? "border-primary-500 focus:border-primary-500 focus:ring-primary-100"
        : "border-background-300 focus:border-primary-400 focus:ring-primary-100"
    }`;

  const labelClass = "mb-1.5 block text-sm font-label font-medium text-foreground-700";
  const errorText = (name: string) =>
    errors[name] ? (
      <p className="mt-1 flex items-center gap-1 text-xs text-primary-700">
        <i className="ri-error-warning-line text-sm leading-none" aria-hidden="true"></i>
        {errors[name]}
      </p>
    ) : null;

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const formData = new FormData(formEl);

    const honeypot = String(formData.get("contact_alt") ?? "").trim();
    if (honeypot) {
      setNumero("AX-000000");
      setStatus("success");
      return;
    }

    const value = (key: string) => String(formData.get(key) ?? "").trim();

    const camposObligatorios: Record<string, string> = {
      nombre_cliente: "Ingresa tu nombre completo.",
      telefono: "Ingresa un teléfono de contacto.",
      email: "Ingresa tu correo electrónico.",
      tipo_servicio: "Selecciona el tipo de servicio.",
      descripcion: "Cuéntanos brevemente qué necesitas.",
    };

    const nuevosErrores: FieldErrors = {};
    Object.entries(camposObligatorios).forEach(([campo, mensaje]) => {
      const val = campo === "tipo_servicio" ? tipoServicio : value(campo);
      if (!val) nuevosErrores[campo] = mensaje;
    });

    const email = value("email");
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      nuevosErrores.email = "Ingresa un correo electrónico válido.";
    }

    if (Object.keys(nuevosErrores).length > 0) {
      setErrors(nuevosErrores);
      setFormError("Por favor verifica los campos marcados.");
      setStatus("error");
      return;
    }

    const unidadId = value("unidad_id");
    const unidadSeleccionada = fleet.find((vehicle) => vehicle.id === unidadId);

    const payload: SolicitudInput = {
      nombre_cliente: value("nombre_cliente"),
      empresa: value("empresa") || undefined,
      telefono: value("telefono"),
      email,
      tipo_servicio: tipoServicio,
      unidad: value("unidad") || unidadSeleccionada?.tipo || undefined,
      unidad_id: unidadId || undefined,
      fecha: value("fecha") || undefined,
      hora: value("hora") || undefined,
      origen: value("origen") || undefined,
      destino: value("destino") || undefined,
      personas: value("personas") ? Number(value("personas")) : null,
      tipo_carga: value("tipo_carga") || undefined,
      descripcion: value("descripcion") || undefined,
      info_adicional: value("info_adicional") || undefined,
      requiere_asesoria: asesoria,
    };

    setErrors({});
    setFormError("");
    setStatus("loading");

    try {
      const { data, error } = await supabase.rpc("crear_solicitud_de_servicio", { payload });

      if (error) throw error;

      const resultado = data as { numero?: string; id?: string } | null;
      const numeroGenerado = resultado?.numero ?? "";
      setNumero(numeroGenerado);
      setStatus("success");
      setAsesoria(false);

      // Notificación por correo (aviso interno + confirmación al cliente).
      // No bloquea la confirmación mostrada al usuario.
      void supabase.functions
        .invoke("notificar-solicitud", {
          body: { solicitud: { ...payload, numero: numeroGenerado, id: resultado?.id } },
        })
        .then(({ data, error }) => {
          if (error) {
            console.warn("No se pudo notificar por correo:", error.message);
            return;
          }
          const respuesta = data as { sent?: boolean; reason?: string; missing?: string[] } | null;
          if (respuesta && respuesta.sent === false) {
            console.warn(
              "El correo no se envió. Motivo:",
              respuesta.reason,
              respuesta.missing ? `Faltan: ${respuesta.missing.join(", ")}` : "",
            );
          }
        });

      setTimeout(() => resultRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 50);
    } catch {
      setStatus("error");
      setFormError(
        "No pudimos registrar tu solicitud. Revisa tu conexión e intenta nuevamente, o contáctanos por teléfono.",
      );
    }
  };

  if (status === "success") {
    return (
      <div
        ref={resultRef}
        className="rounded-lg border border-accent-300 bg-background-50 p-6 text-center md:p-10"
      >
        <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-accent-100 text-accent-700">
          <i className="ri-checkbox-circle-line text-3xl leading-none" aria-hidden="true"></i>
        </span>
        <h2 className="mt-5 text-2xl font-heading font-bold text-foreground-950 md:text-3xl">
          Solicitud recibida correctamente
        </h2>
        <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-foreground-600">
          Recibimos tu solicitud. Nuestro equipo revisará la información y se pondrá en contacto
          contigo para coordinar los detalles del servicio.
        </p>

        <div className="mx-auto mt-6 inline-flex flex-col items-center rounded-lg border border-background-200 bg-background-100 px-8 py-5">
          <span className="text-xs font-label font-semibold uppercase tracking-[0.18em] text-foreground-500">
            Número de solicitud
          </span>
          <span className="mt-1 text-2xl font-heading font-bold tracking-wide text-primary-700 md:text-3xl">
            {numero}
          </span>
        </div>

        <p className="mx-auto mt-5 max-w-md text-xs text-foreground-500">
          Guarda este número para dar seguimiento a tu solicitud. Si lo necesitas, también puedes
          escribirnos o llamarnos.
        </p>

        <div className="mt-7 flex flex-col justify-center gap-3 sm:flex-row">
          <Button variant="primary" size="md" icon="ri-add-line" onClick={resetFormulario}>
            Solicitar otro servicio
          </Button>
          <Button to="/" variant="outline" size="md" icon="ri-home-5-line">
            Volver al inicio
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form
      ref={formRef}
      onSubmit={handleSubmit}
      noValidate
      className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8"
    >
      {unidadSeleccionadaBanner(defaultUnidadId, fleet)}

      {/* Información del cliente */}
      <fieldset className="space-y-4">
        <legend className="mb-1 flex items-center gap-2 text-base font-heading font-semibold text-foreground-950">
          <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary-100 text-sm font-label font-semibold text-primary-700">
            1
          </span>
          Información del cliente
        </legend>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="nombre_cliente" className={labelClass}>
              Nombre completo <span className="text-primary-600">*</span>
            </label>
            <input
              id="nombre_cliente"
              name="nombre_cliente"
              type="text"
              autoComplete="name"
              placeholder="Ej. María Fernández"
              className={fieldClass("nombre_cliente")}
            />
            {errorText("nombre_cliente")}
          </div>
          <div>
            <label htmlFor="empresa" className={labelClass}>
              Empresa <span className="text-foreground-400">(opcional)</span>
            </label>
            <input
              id="empresa"
              name="empresa"
              type="text"
              autoComplete="organization"
              placeholder="Ej. Distribuidora Andina S.A.C."
              className={fieldClass("empresa")}
            />
          </div>
          <div>
            <label htmlFor="telefono" className={labelClass}>
              Teléfono <span className="text-primary-600">*</span>
            </label>
            <input
              id="telefono"
              name="telefono"
              type="tel"
              autoComplete="tel"
              placeholder="+51 987 654 321"
              className={fieldClass("telefono")}
            />
            {errorText("telefono")}
          </div>
          <div>
            <label htmlFor="email" className={labelClass}>
              Correo electrónico <span className="text-primary-600">*</span>
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              placeholder="tucorreo@ejemplo.com"
              className={fieldClass("email")}
            />
            {errorText("email")}
          </div>
        </div>
      </fieldset>

      <hr className="my-7 border-background-200" />

      {/* Tipo de servicio */}
      <fieldset className="space-y-4">
        <legend className="mb-1 flex items-center gap-2 text-base font-heading font-semibold text-foreground-950">
          <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary-100 text-sm font-label font-semibold text-primary-700">
            2
          </span>
          Servicio y unidad requerida
        </legend>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="tipo_servicio" className={labelClass}>
              Tipo de servicio <span className="text-primary-600">*</span>
            </label>
            <select
              id="tipo_servicio"
              name="tipo_servicio"
              value={tipoServicio}
              onChange={(event) => setTipoServicio(event.target.value)}
              className={fieldClass("tipo_servicio")}
            >
              <option value="">Selecciona un servicio</option>
              {tipoServicioOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            {errorText("tipo_servicio")}
          </div>

          <div>
            <label htmlFor="unidad" className={labelClass}>
              Unidad requerida
            </label>
            <select id="unidad" name="unidad" defaultValue="" className={fieldClass("unidad")}>
              <option value="">Aún no lo sé / me asesoran</option>
              {unidadTipoOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label htmlFor="unidad_id" className={labelClass}>
            Unidad específica del catálogo <span className="text-foreground-400">(opcional)</span>
          </label>
          <select
            id="unidad_id"
            name="unidad_id"
            defaultValue={defaultUnidadId}
            className={fieldClass("unidad_id")}
          >
            <option value="">Ninguna en particular</option>
            {fleet.map((vehicle) => (
              <option key={vehicle.id} value={vehicle.id}>
                {vehicle.nombre} — {vehicle.tipo}
              </option>
            ))}
          </select>
          <p className="mt-1.5 text-xs text-foreground-500">
            Puedes elegir una unidad de nuestro catálogo o dejar que te recomendemos la ideal.
          </p>
        </div>

        {mostrarPersonas && (
          <div className="sm:max-w-xs">
            <label htmlFor="personas" className={labelClass}>
              Cantidad de personas
            </label>
            <input
              id="personas"
              name="personas"
              type="number"
              min={1}
              placeholder="Ej. 25"
              className={fieldClass("personas")}
            />
          </div>
        )}

        <label
          htmlFor="requiere_asesoria"
          className="flex cursor-pointer items-start gap-3 rounded-md border border-background-300 bg-background-100 p-4"
        >
          <input
            id="requiere_asesoria"
            name="requiere_asesoria"
            type="checkbox"
            checked={asesoria}
            onChange={(event) => setAsesoria(event.target.checked)}
            className="mt-0.5 h-4 w-4 shrink-0 rounded border-background-400 text-primary-600 focus:ring-primary-400"
          />
          <span>
            <span className="block text-sm font-label font-semibold text-foreground-900">
              Necesito asesoría para elegir la unidad adecuada
            </span>
            <span className="mt-0.5 block text-xs text-foreground-500">
              Nuestro equipo te recomendará la unidad y el servicio más convenientes.
            </span>
          </span>
        </label>
      </fieldset>

      <hr className="my-7 border-background-200" />

      {/* Información del servicio */}
      <fieldset className="space-y-4">
        <legend className="mb-1 flex items-center gap-2 text-base font-heading font-semibold text-foreground-950">
          <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary-100 text-sm font-label font-semibold text-primary-700">
            3
          </span>
          Información del servicio
        </legend>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="fecha" className={labelClass}>
              Fecha del servicio
            </label>
            <input id="fecha" name="fecha" type="date" className={fieldClass("fecha")} />
          </div>
          <div>
            <label htmlFor="hora" className={labelClass}>
              Hora aproximada
            </label>
            <input id="hora" name="hora" type="time" className={fieldClass("hora")} />
          </div>
          <div>
            <label htmlFor="origen" className={labelClass}>
              Lugar de recogida
            </label>
            <input
              id="origen"
              name="origen"
              type="text"
              placeholder="Ej. Av. Los Álamos 320, Surco"
              className={fieldClass("origen")}
            />
          </div>
          <div>
            <label htmlFor="destino" className={labelClass}>
              Lugar de destino
            </label>
            <input
              id="destino"
              name="destino"
              type="text"
              placeholder="Ej. Calle Las Begonias 120, San Isidro"
              className={fieldClass("destino")}
            />
          </div>
        </div>

        <div className="sm:max-w-md">
          <label htmlFor="tipo_carga" className={labelClass}>
            Tipo de carga
          </label>
          <select id="tipo_carga" name="tipo_carga" defaultValue="" className={fieldClass("tipo_carga")}>
            <option value="">Selecciona el tipo de carga</option>
            {tipoCargaOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="descripcion" className={labelClass}>
            Descripción de lo que necesitas <span className="text-primary-600">*</span>
          </label>
          <textarea
            id="descripcion"
            name="descripcion"
            rows={4}
            maxLength={500}
            placeholder="Cuéntanos qué necesitas mover, cantidad estimada, accesos y cualquier detalle relevante (máx. 500 caracteres)."
            className={`${fieldClass("descripcion")} resize-none`}
          ></textarea>
          {errorText("descripcion")}
        </div>

        <div>
          <label htmlFor="info_adicional" className={labelClass}>
            Información adicional <span className="text-foreground-400">(opcional)</span>
          </label>
          <textarea
            id="info_adicional"
            name="info_adicional"
            rows={3}
            maxLength={500}
            placeholder="Horarios especiales, requisitos de acceso, necesidades de embalaje, etc."
            className={`${fieldClass("info_adicional")} resize-none`}
          ></textarea>
        </div>
      </fieldset>

      <div className="axonia-reserved-field" aria-hidden="true">
        <label htmlFor="solicitud-contact-alt">No completar este campo</label>
        <input
          id="solicitud-contact-alt"
          name="contact_alt"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          readOnly
        />
      </div>

      {status === "error" && formError && (
        <p
          role="alert"
          className="mt-6 flex items-start gap-2 rounded-md bg-primary-50 px-3.5 py-3 text-sm text-primary-800"
        >
          <i className="ri-error-warning-line mt-0.5 text-base leading-none" aria-hidden="true"></i>
          {formError}
        </p>
      )}

      <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-foreground-500">
          Los campos marcados con <span className="text-primary-600">*</span> son obligatorios.
        </p>
        <Button
          type="submit"
          variant="primary"
          size="lg"
          icon={status === "loading" ? "ri-loader-4-line" : "ri-send-plane-line"}
          disabled={status === "loading"}
          className="sm:w-auto w-full"
        >
          {status === "loading" ? "Enviando solicitud..." : "Enviar solicitud"}
        </Button>
      </div>
    </form>
  );
}

function unidadSeleccionadaBanner(unidadId: string, fleet: { id: string; nombre: string }[]) {
  const vehicle = fleet.find((item) => item.id === unidadId);
  if (!vehicle) return null;
  return (
    <div className="mb-6 flex items-start gap-3 rounded-md border border-accent-300 bg-accent-50 px-4 py-3">
      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-700">
        <i className="ri-truck-line text-base leading-none" aria-hidden="true"></i>
      </span>
      <p className="text-sm text-accent-900">
        Estás solicitando la unidad <strong className="font-semibold">{vehicle.nombre}</strong>. Puedes
        cambiarla más abajo si lo necesitas.
      </p>
    </div>
  );
}